import { action, computed, flow, makeObservable, observable } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { CreditAuthorizationPersonalInfoFormStore } from './creditAuthorizationPersonalInfoFormStore';
import { PublicBorrowerSetupToDoService } from 'libs/feature-borrowers/src/borrowerSetupToDo/services/publicBorrowerSetupToDoService';
import { formatDate, isBlank } from '@roc/feature-utils';
import { differenceInYears, parse, startOfToday } from 'date-fns';
import { getMultiPageScreenshotGridWaitingForImages } from '@roc/ui';

export class PublicCreditAuthorizationStore {
  private globalStore: GlobalStore;
  personalInformationFormStore: CreditAuthorizationPersonalInfoFormStore;
  publicBorrowerSetupToDoService: PublicBorrowerSetupToDoService;
  activeStep: number;
  invalidSSN: string[];
  creditAuthorizationScreenshotIds: string[];
  pdf: any;
  disableNameFields: boolean;
  requestStatus: string;

  constructor(globalStore) {
    this.globalStore = globalStore;
    this.personalInformationFormStore = new CreditAuthorizationPersonalInfoFormStore(globalStore);
    this.publicBorrowerSetupToDoService = new PublicBorrowerSetupToDoService();
    this.creditAuthorizationScreenshotIds = ['projectInformation'];

    makeObservable(this, {
      resetStore: flow,
      fetchInvalidSSN: flow,
      validateStep: action,
      isInvalidSSN: action,
      isInvalidDateOfBirthDate: action,
      showScreenshotGrid: flow,
      hideScreenshotGrid: flow,
      creditAuthorizationScreenshotIds: observable,
      setCreditAuthorizationScreenshotIds: flow,
      savePdfOnDropbox: flow,
      pdf: observable,
      getPdf: computed,
      fetchBorrowerDetailsVerification: flow,
      disableNameFields: observable,
      requestStatus: observable,
    });
    this.setDefaults();
  }

  *fetchInvalidSSN() {
    try {
      const response = yield this.publicBorrowerSetupToDoService.getInvalidSSN();
      this.invalidSSN = response.data.data;
    } catch (e) {
    }
  }

  /**
   * Returns false when the requestToken doesn't match an existing request
   * (the backend fails or returns no data for unknown tokens).
   */
  *fetchBorrowerDetailsVerification(requestToken: string) {
    try {
      const response = yield this.publicBorrowerSetupToDoService.getBorrowerDetailsVerification(requestToken);
      const data = response.data.data;
      if (!data) {
        return false;
      }
      const { firstName, lastName } = data?.savedForm ?? {};
      // Lock the name fields only when they both actually came back with value.
      this.disableNameFields = !!firstName && !!lastName;
      this.personalInformationFormStore.loadForm({
        firstName,
        lastName
      });
      this.requestStatus = data.requestState;
      return true;
    } catch (e) {
      return false;
    }
  }

  private setDefaults() {
    this.invalidSSN = [];
    this.disableNameFields = false;
    this.requestStatus = null;
  }

  *resetStore() {
    this.personalInformationFormStore.reset();
    this.setDefaults();
  }

  isInvalidSSN(ssn: string) {
    return this.invalidSSN?.includes(ssn);
  }

  isInvalidDateOfBirthDate = (dateOfBirth) => {
    const date = formatDate(dateOfBirth, 'MM/dd/yyyy')
    return (
      dateOfBirth &&
      differenceInYears(startOfToday(), parse(date, 'MM/dd/yyyy', new Date())) < 18
    );
  }

  validateStep() {
    const isInvalidDob = this.isInvalidDateOfBirthDate(this.personalInformationFormStore.form.fields.dateOfBirth.value);
    const isInvalidSSN = this.isInvalidSSN(this.personalInformationFormStore.form.fields.ssn.value);
    const isBlankPhoneNumber = isBlank(this.personalInformationFormStore.form.fields.phoneNumber.value);
    const isInvalidAddress = this.personalInformationFormStore.form.fields.__isAddressInCorrectFormat__.value == false;

    if (isInvalidDob) this.personalInformationFormStore.form.fields.dateOfBirth.error = 'Age must be over 18 years';
    if (isInvalidSSN) this.personalInformationFormStore.form.fields.ssn.error = 'Invalid SSN number.';
    if (isBlankPhoneNumber) this.personalInformationFormStore.form.fields.phoneNumber.error = 'Phone number is required.';
    if (isInvalidAddress) this.personalInformationFormStore.form.fields.address.error = 'Invalid address format';

    if (isInvalidDob || isInvalidAddress || isBlankPhoneNumber || isInvalidSSN || isInvalidDob) {
      this.globalStore.notificationStore.showWarningNotification({ message: 'Please fix the validation errors.', });
      return false;
    }

    return this.personalInformationFormStore.runFormValidationWithMessage();
  }

  showScreenshotGrid() {
    const parentTag = document.getElementById('screenshotParent');
    if (parentTag) {
      parentTag.style.position = 'absolute';
      parentTag.style.top = '0';
      parentTag.style.zIndex = '-9999';
      parentTag.style.display = 'block';
      parentTag.style.width = '841px';
    }
  }

  hideScreenshotGrid() {
    const parentTag = document.getElementById('screenshotParent');
    if (parentTag) {
      parentTag.style.display = 'none';
    }
  }

  *savePdfOnDropbox(onSuccess: () => void) {
    this.showScreenshotGrid();
    const pdf = yield getMultiPageScreenshotGridWaitingForImages('Caf', this.creditAuthorizationScreenshotIds, []);
    this.pdf = pdf;
    this.hideScreenshotGrid()
    onSuccess()
  }

  setCreditAuthorizationScreenshotIds(creditAuthorizationScreenshotIds) {
    this.creditAuthorizationScreenshotIds = creditAuthorizationScreenshotIds;
  }

  get getPdf() {
    return this.pdf
  }

}